import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { AuthService } from 'src/app/core/services/auth.service';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class McpService {
  private readonly defaultServerUrl = environment.mcpApiRootUrl || 'https://buyercaddy-api.readme.io/mcp';
  private messageIdCounter = 1;
  private sessionId: string | null = null;

  constructor(private http: HttpClient, private authService: AuthService) {
    this.sessionId = this.generateGuid();
  }

  private generateGuid(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  private async sendRequest(method: string, params: any = {}): Promise<any> {
    const id = this.messageIdCounter++;
    const payload = {
      jsonrpc: '2.0',
      id,
      method,
      params
    };

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Accept': 'application/json, text/event-stream'
    });

    const url = `${this.defaultServerUrl}?sessionId=${this.sessionId}`;

    try {
      const responseText = await firstValueFrom(
        this.http.post(url, payload, { headers, responseType: 'text' })
      );

      return this.parseSseResponse(responseText);
    } catch (error) {
      console.error(`MCP request failed for ${method}:`, error);
      throw error;
    }
  }

  private parseSseResponse(text: string): any {
    if (!text) {
      throw new Error('Empty MCP response');
    }

    // Try parsing as raw JSON first (in case proxy strips SSE formatting)
    try {
      const jsonData = JSON.parse(text);
      if (jsonData.result || jsonData.error) {
         if (jsonData.error) throw new Error(jsonData.error.message);
         return jsonData.result;
      }
    } catch (e) {
      // Not raw JSON, continue to SSE parsing
    }

    const lines = text.split('\n');
    let dataStr = '';
    
    for (const line of lines) {
      if (line.startsWith('data: ')) {
        dataStr = line.substring(6);
        break;
      }
      if (line.startsWith('{') && line.includes('"jsonrpc"')) {
         dataStr = line;
         break;
      }
    }

    if (!dataStr) {
      throw new Error('Invalid MCP response format. Response: ' + text);
    }

    const data = JSON.parse(dataStr);
    
    if (data.error) {
       throw new Error(data.error.message || JSON.stringify(data.error));
    }
    
    return data.result || data; 
  }

  async getTools(): Promise<any> {
    return this.sendRequest('tools/list');
  }

  async callTool(toolName: string, args: any): Promise<any> {
    return this.sendRequest('tools/call', {
      name: toolName,
      arguments: args
    });
  }

  async connect(): Promise<void> {
    // No persistent connection needed for this transport type
    return Promise.resolve();
  }

  async disconnect(): Promise<void> {
    // No persistent connection to close
    return Promise.resolve();
  }
}
